import { useState, type ReactElement } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';

// captomi_office admin actions, tenant-scoped (issue #15 WS2). Relocated from the user-scoped
// UserAppSection now that office app-data lives at tenant scope. Grant-addon is fully
// tenant-scoped (WS1 endpoint); the receptionist actions still take a user_id for the
// dialed-number registry — auto-resolved to the tenant's single office user (a selector is a
// future for multi-user tenants).
//
// The receptionist buttons are gated on the live provisioning status so an operator only ever sees
// the ONE valid next step: Provision when there's no number, Mark-active only after a number exists
// and has been bound in Retell, and nothing once ACTIVE. (Provision is idempotent server-side, but
// showing it on an already-provisioned tenant is confusing — issue raised 2026-06-18.)

type ReceptionistStatus =
  | 'NOT_PROVISIONED'
  | 'PROVISIONING'
  | 'AWAITING_RETELL_BIND'
  | 'ACTIVE'
  | 'RELEASED';

interface OfficeAppDataResponse {
  data?: {
    phone_numbers?: {
      receptionist_number?: string | null;
      receptionist_number_status?: ReceptionistStatus;
    };
  };
}

function ActionButton({
  label,
  confirm,
  run,
}: {
  label: string;
  confirm: string;
  run: () => Promise<string>;
}): ReactElement {
  const [busy, set_busy] = useState(false);
  const [result, set_result] = useState<string | null>(null);
  const [error, set_error] = useState<string | null>(null);
  async function go() {
    if (!window.confirm(confirm)) return;
    set_busy(true);
    set_error(null);
    set_result(null);
    try {
      set_result(await run());
    } catch (err) {
      set_error(err instanceof ApiError ? err.reason : String(err));
    } finally {
      set_busy(false);
    }
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <button type="button" disabled={busy} onClick={go}>
        {busy ? '…' : label}
      </button>
      {result && <span className="muted" style={{ fontSize: 13 }}>{result}</span>}
      {error && <span className="error" style={{ fontSize: 13 }}>{error}</span>}
    </div>
  );
}

export default function TenantOfficeActions({
  tenant_id,
  user_id,
}: {
  tenant_id: string;
  /** The tenant's single office user — for the receptionist-number registry. null = none yet. */
  user_id: string | null;
}): ReactElement {
  const queryClient = useQueryClient();
  // Share the SchemaForm's query key (same endpoint) so the page makes ONE request and a
  // Provision/Mark-active refresh propagates to the schema-driven editor above too — and vice
  // versa. A separate key would re-introduce the stale-button bug this component fixes.
  const officeKey = ['tenant-app', tenant_id, 'captomi_office'];
  const { data, isLoading, isError } = useQuery({
    queryKey: officeKey,
    queryFn: () => api.get<OfficeAppDataResponse>(`/api/tenants/${tenant_id}/apps/captomi_office`),
  });
  // After a provision / mark-active write, refetch so the buttons reflect the new status.
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: officeKey });

  const pn = data?.data?.phone_numbers;
  const status: ReceptionistStatus = pn?.receptionist_number_status ?? 'NOT_PROVISIONED';
  const e164 = typeof pn?.receptionist_number === 'string' ? pn.receptionist_number : '';

  const canProvision = status === 'NOT_PROVISIONED' || status === 'RELEASED';
  const awaitingBind = status === 'AWAITING_RETELL_BIND';

  return (
    <div className="card" style={{ marginBottom: 24, display: 'grid', gap: 4 }}>
      <strong style={{ fontSize: 14, marginBottom: 4 }}>Office actions</strong>

      <ActionButton
        label="Grant add-on"
        confirm="Grant one billing add-on (minutes + messages) to this tenant's current cycle?"
        run={async () => {
          const r = await api.post<{ cycle_addons: { minutes: number; messages: number } }>(
            `/api/tenants/${tenant_id}/apps/captomi_office/grant-addon`,
            {},
          );
          return `Added — cycle add-ons now ${r.cycle_addons.minutes} min / ${r.cycle_addons.messages} msg`;
        }}
      />

      {user_id ? (
        <>
          <div className="muted" style={{ fontSize: 12, margin: '2px 0 6px' }}>
            Receptionist number: <strong>{e164 || '—'}</strong> · status{' '}
            <strong>{isLoading ? '…' : isError ? 'unavailable' : status}</strong>
          </div>

          {isLoading ? null : isError ? (
            <span className="error" style={{ fontSize: 12 }}>
              Couldn't load receptionist status — refresh the page before provisioning.
            </span>
          ) : canProvision ? (
            <ActionButton
              label="Provision receptionist number"
              confirm="Buy + provision a receptionist number for this tenant?"
              run={async () => {
                const r = await api.post<{ status: string; e164: string }>(
                  '/api/captomi-office/receptionist-numbers/provision',
                  { tenant_id, user_id },
                );
                await refresh();
                return `${r.status}${r.e164 ? ` — ${r.e164}` : ''}`;
              }}
            />
          ) : status === 'PROVISIONING' ? (
            <span className="muted" style={{ fontSize: 12 }}>
              Provisioning in flight — refresh in a moment.
            </span>
          ) : awaitingBind ? (
            <ActionButton
              label="Activate receptionist line"
              confirm="Activate this line? It will start answering real customer calls."
              run={async () => {
                const r = await api.post<{ status: string; e164: string }>(
                  '/api/captomi-office/receptionist-numbers/mark-active',
                  { tenant_id, user_id },
                );
                await refresh();
                return `${r.status}${r.e164 ? ` — ${r.e164}` : ''}`;
              }}
            />
          ) : (
            <span className="muted" style={{ fontSize: 12 }}>
              ✓ Active{e164 ? ` — ${e164}` : ''}
            </span>
          )}
          {/* The Retell bind is done by the provisioning pipeline now — telling an operator to go
              and do it by hand would send them to the Console to redo work that already happened.
              This state means "bought, trunked, A2P-registered, BOUND — waiting on a human to say
              it's fine to go live", which is only reachable from onboarding when
              receptionist_number_auto_activate is off. */}
          {awaitingBind && (
            <span className="muted" style={{ fontSize: 11 }}>
              {e164 || 'The number'} is bought and already bound to the receptionist agent. Check the
              tenant's profile looks right, then Activate to start taking real calls.
            </span>
          )}
        </>
      ) : (
        <span className="muted" style={{ fontSize: 12 }}>
          Add a user to this tenant to provision a receptionist number.
        </span>
      )}
    </div>
  );
}
